/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {
    Debug,
    ViewModeSingle, ViewModeQuad, ViewModeStereo, ViewModeWebVR,
    CameraAnimator as RenderCameraAnimator,
    ViewMode as RenderViewMode,
    InterpolatorsFactory,
    Quaternion,
    RenderDocument,
    BoundingBox as RenderBoundingBox} from "js_renderer";

import AbstractModule from "../abstract_module";
import {ViewModeEnum} from "./scene_view_mode_enums";
import {vec2, vec3} from "gl-matrix";
import BoundingBox from "../../bounding_box";
import IModel from "../../structural_elements/model_interface";
import IViewModeModule from "./scene_view_mode_interface";
import Scene from "../../scene";
import PrimitivePointPicker from "../picking/primitive_point_picker";
import IViewControls from "../view_controls/scene_view_controls_interface";
import {EViewControls} from "../view_controls/scene_view_controls_enums";

export default
class ViewModeModule extends AbstractModule implements IViewModeModule {

    protected static _typeName: string = "viewMode";

    private _mode: RenderViewMode;
    private _viewModeType: ViewModeEnum;
    private _viewControlsType: EViewControls = null;

    private frustumCullingEnabled: boolean = false;

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        this._type = ViewModeModule._typeName;

        this._mode = null;

        // default initialization
        this.setViewMode(ViewModeEnum.SINGLE);
        // this._mode.setViewControls(EViewControls.EXAMINER, null);
    }

    public static getClassType(): string {
        return ViewModeModule._typeName;
    }

    // this is for WebVR
    public requestPresent() {
        this._mode.requestPresent();
    }

    // this is for WebVR
    public exitPresent() {
        this._mode.exitPresent();
    }

    /**
     * Zoom the camera to center of the canvas for all views
     *
     * @param {number} aDelta zoom delta
     */
    public zoom(aDelta: number) {
        if (aDelta === 0) {
            return;
        }

        // the aDelta value comes from the compas GUI and is in interval between <-1,1> but the standard
        // delta value is expected in interval <-12,12> (do not ask me why :D)
        let views = this._mode.getViews();
        for (let v in views) {
            let view = views[v];
            view.onWheel(view.size[0] / 2.0, view.size[1] / 2.0, aDelta * 12, 8);
            view.viewpointChanged();
        }
    }

    public notifyViewPointChanged() {
        return this._mode.viewpointChanged();
    }

    public viewpointChanged(): any {
    }

    /**
     * Return array of views that should be updated according to a viewMode type.
     * For stereo and vr mode set view to all mode's views, otherwise to active one only.
     *
     * @return {Array} Array of view objects. This is break of encapsulation of view as they do not have api
     */
    private getViewsToUpdateByViewMode(): any[] {
        let views = [];
        if (this._viewModeType === ViewModeEnum.STEREO ||
            this._viewModeType === ViewModeEnum.WEBVR) {
            views = this._mode.getViews();
        } else {
            views = [this.getActiveView()];
        }
        return views;
    }

    // this breaks the encapuslation and should be replaced via call to this module or by implementing the scne api View class and its interface
    public getViews(): any[] {
        return this._mode.getViews();
    }

    /**
     * Zoom to the geometry included in the group
     *
     * @param {number} an array of serialized group tree items array returned from scne api document/Model as aresnose to getGroupttreeStructure
     */
    public zoomToGroup(aSerializedGroupTreeItemsArray: any[]) {
        let groupsBbox = new BoundingBox();

        for (let g = 0; g < aSerializedGroupTreeItemsArray.length; g++) {
            let gtItem = aSerializedGroupTreeItemsArray[g];

            // then we take the main models's zeropoint rotation and add it to the yRotation
            let model = this.sceneAPI.getDocument().getModelByUID(gtItem.sceneAPIModelUID);
            if (!model) {
                continue;
            }

            let bbox = model.getSGroupBoundingBox(gtItem.group_id);
            groupsBbox.extend(bbox);
        }

        if (!groupsBbox.isValid()) {
            return;
        }

        let manipulatedCenter = this.sceneAPI.getActivityModule().transformByCurrentManipulatorTransformation(groupsBbox.getCenter());

        let views = this.getViewsToUpdateByViewMode();
        for (let v = 0; v !== views.length; v++) {
            let cameraAnimator = new RenderCameraAnimator(this.sceneAPI);
            cameraAnimator.setInterpolator(
                InterpolatorsFactory.create(
                    Quaternion.TypeName
                )
            );
            cameraAnimator.preventTexturesSwitching(true);

            let camera = views[v].getCamera();
            let position = vec3.clone(camera.getCameraPosition());

            let camDir = camera.getCameraDirection();//groupsBbox.getOptimalViewVector();
            vec3.scaleAndAdd(position, camera.getCameraTarget(), camDir, -groupsBbox.getRadius() * 1.2);

            let startQuat = RenderViewMode.computeQuaternion(camera.getViewMatrix());
            let endQuat = RenderViewMode.computeQuaternion(camera.getCustomViewMatrix(position, manipulatedCenter));

            cameraAnimator.setAnimationFrames([
                {
                    position: vec3.clone(camera.getCameraPosition()),
                    target: vec3.clone(camera.getCameraTarget()),
                    quat: startQuat
                },
                {
                    position: position,
                    target: manipulatedCenter,
                    quat: endQuat
                }
            ]);

            this.canvas.addAnimatedObject(this.getActiveView().getViewControls(), cameraAnimator);
        }
    }

    /***
     * Animate camera to specified position to a distance specified by bounding box radius multiplied by aFocusFactor.
     *
     * @param {vec3} aPosition
     * @param {float} aFocusFactor distance between camera position and the focused point as ratio between the scene bbox and the factor.
     * @param {float} aAnimationTime Length of the animation in seconds
     */
    public focusToPosition(aPosition: vec3, aFocusFactor: number, aAnimationTime: number) {
        let view = this.getActiveView();
        if (view) {

            let cameraPos = vec3.clone(view.getCamera().getCameraPosition());
            let pickedPointMs = vec3.clone(aPosition);
            let factor = aFocusFactor;

            let direction = vec3.create();
            vec3.subtract(direction, pickedPointMs, cameraPos);

            let distance = vec3.length(direction);
            let insideBB = view.getDocument().getBoundingBox().containsPoint(view.getCamera().getCameraPosition());

            if (insideBB && distance <= 2 * view.getCamera().getNearValue()) {
                return;
            } // prevent near plane clipping when inside model BBox

            let space = distance * (1.0 - aFocusFactor);

            if (space <= view.getCamera().getNearValue()) {
                factor = (view.getCamera().getNearValue() / distance) - 0.05; // go not so close for sure
            }

            vec3.scale(direction, direction, factor);
            vec3.add(cameraPos, cameraPos, direction);

            this._mode.animateView(cameraPos, pickedPointMs, view.getCamera().getFieldOfView(), 1.0, null);
            // this.restrictCameraToGround(); // shall be done as well
        }
    }

    /***
     * Animate camera to specified position to a distance specified by bounding box radius multiplied by aFocusFactor.
     *
     * @param {vec3} aPosition
     * @param {float} aAnimationTime Length of the animation in seconds
     * @param {float} aHeight Final point z-axis elevation
     */
    public goToPosition(aPosition: vec3, aAnimationTime: number, aHeight: number) {
        let localPoint = vec3.clone(aPosition);

        let target = vec3.create();

        let view = this.getActiveView();
        if (view) {
            let direction = vec3.create();
            vec3.subtract(direction, view.getCamera().getCameraTarget(), view.getCamera().getCameraPosition());
            vec3.normalize(direction, direction);

            target[0] = localPoint[0] + direction[0];
            target[1] = localPoint[1];
            target[2] = localPoint[2] + direction[2];
        }

        localPoint[1] = localPoint[1] + aHeight;

        this._mode.animateView(localPoint, target, view.getCamera().getFieldOfView(), aAnimationTime, null);
    }

    public setView(aPosition: vec3, aTarget: vec3, aFov: number) {
        this._mode.setView(aPosition, aTarget, aFov);
    }

    public setActiveView(aPosition: vec3, aTarget: vec3, aFov: number) {
        this._mode.setActiveView(aPosition, aTarget, aFov);
    }

    public getActiveView(): any {
        return this._mode.getActiveView();
    }

    public animateView(aPosition: vec3, aTarget: vec3, aFov: number, aAnimationTime: number, aAnimationFinishCB: any)
    {
        this._mode.animateView(aPosition, aTarget, aFov, aAnimationTime, aAnimationFinishCB);
    }

    public animateAllViews(aPosition: vec3, aTarget: vec3, aFov: number,  aAnimationTime: number, aAnimationFinishCB: any)
    {
        this._mode.animateAllViews(aPosition, aTarget, aFov, aAnimationTime, aAnimationFinishCB);
    }

    /***
     * Set left view to scene content or aObject in the argument if defined.
     *
     * @param aObject an object which has getSBoundingBox method implemented
     */
    public setLeftView(aObject: IModel) {
        if (!this.sceneDocument) {
            return false;
        }

        let _sceneAPIBBox = this.sceneDocument.getSBoundingBox();

        let manipulatedCenter = _sceneAPIBBox.getCenter();
        // if the aObject is defined and has a method getBoundingBox, then we use the bounding box from it
        //@ts-ignore
        if (aObject && aObject !== undefined && aObject.getSBoundingBox !== undefined) {
            _sceneAPIBBox = aObject.getSBoundingBoxFromVisibleObjects();

            // get translation from manipulator transformation
            manipulatedCenter = this.sceneAPI.getActivityModule().transformByCurrentManipulatorTransformation(_sceneAPIBBox.getCenter());
        }

        let position = vec3.clone(manipulatedCenter);
        position[0] = position[0] - _sceneAPIBBox.getDiameter();

        // for stereo and vr mode set view to all mode's views, otherwise to active one only
        let views = this.getViewsToUpdateByViewMode();

        for (let i = 0; i !== views.length; i++) {
            let cameraAnimator = new RenderCameraAnimator(this.sceneAPI);
            cameraAnimator.setInterpolator(
                InterpolatorsFactory.create(
                    Quaternion.TypeName
                )
            );
            cameraAnimator.preventTexturesSwitching(true);

            let camera = views[i].getCamera();

            let startQuat = RenderViewMode.computeQuaternion(camera.getViewMatrix());
            let endQuat = RenderViewMode.computeQuaternion(camera.getCustomViewMatrix(position, manipulatedCenter));

            cameraAnimator.setAnimationFrames([
                {
                    position: vec3.clone(camera.getCameraPosition()),
                    target: vec3.clone(camera.getCameraTarget()),
                    quat: startQuat
                },
                {
                    position: position,
                    target: manipulatedCenter,
                    quat: endQuat
                }
            ]);
            this.canvas.addAnimatedObject(this.getActiveView().getViewControls(), cameraAnimator);
        }

        return true;
    }

    public setRightView(aObject: IModel) {
        if (!this.sceneDocument) {
            return false;
        }

        let _sceneAPIBBox = this.sceneDocument.getSBoundingBox();

        let manipulatedCenter = _sceneAPIBBox.getCenter();
        // if the aObject is defined and has a method getBoundingBox, then we use the bounding box from it
        //@ts-ignore
        if (aObject && aObject !== undefined && aObject.getSBoundingBox !== undefined) {
            _sceneAPIBBox = aObject.getSBoundingBoxFromVisibleObjects();

            // get translation from manipulator transformation
            manipulatedCenter = this.sceneAPI.getActivityModule().transformByCurrentManipulatorTransformation(_sceneAPIBBox.getCenter());
        }

        let position = vec3.clone(manipulatedCenter);
        position[0] = position[0] + _sceneAPIBBox.getDiameter();

        // for stereo and vr mode set view to all mode's views, otherwise to active one only
        let views = this.getViewsToUpdateByViewMode();

        for (let i = 0; i !== views.length; i++) {
            let cameraAnimator = new RenderCameraAnimator(this.sceneAPI);
            cameraAnimator.setInterpolator(
                InterpolatorsFactory.create(
                    Quaternion.TypeName
                )
            );
            cameraAnimator.preventTexturesSwitching(true);

            let camera = views[i].getCamera();

            let startQuat = RenderViewMode.computeQuaternion(camera.getViewMatrix());
            let endQuat = RenderViewMode.computeQuaternion(camera.getCustomViewMatrix(position, manipulatedCenter));

            cameraAnimator.setAnimationFrames([
                {
                    position: vec3.clone(camera.getCameraPosition()),
                    target: vec3.clone(camera.getCameraTarget()),
                    quat: startQuat
                },
                {
                    position: position,
                    target: manipulatedCenter,
                    quat: endQuat
                }
            ]);

            this.canvas.addAnimatedObject(this.getActiveView().getViewControls(), cameraAnimator);
        }
    }

    public setTopView(aObject: IModel) {
        if (!this.sceneDocument) {
            return false;
        }

        let _sceneAPIBBox = this.sceneDocument.getSBoundingBox();

        let manipulatedCenter = _sceneAPIBBox.getCenter();
        // if the aObject is defined and has a method getBoundingBox, then we use the bounding box from it
        //@ts-ignore
        if (aObject && aObject !== undefined && aObject.getSBoundingBox !== undefined) {
            _sceneAPIBBox = aObject.getSBoundingBoxFromVisibleObjects();

            // get translation from manipulator transformation
            manipulatedCenter = this.sceneAPI.getActivityModule().transformByCurrentManipulatorTransformation(_sceneAPIBBox.getCenter());
        }

        // for stereo and vr mode set view to all mode's views, otherwise to active one only
        let views = this.getViewsToUpdateByViewMode();

        for (let i = 0; i !== views.length; i++) {
            let cameraAnimator = new RenderCameraAnimator(this.sceneAPI);
            cameraAnimator.setInterpolator(
                InterpolatorsFactory.create(
                    Quaternion.TypeName
                )
            );
            cameraAnimator.preventTexturesSwitching(true);

            let camera = views[i].getCamera();

            let position = vec3.clone(manipulatedCenter);
            position[1] = position[1] + _sceneAPIBBox.getDiameter();

            if (camera.getCameraPosition()[2] < position[2]) {
                position[2] = position[2] - 0.1;// prevent the 0 -1 0 view vector
            } else {
                position[2] = position[2] + 0.1;// prevent the 0 -1 0 view vector
            }

            let startQuat = RenderViewMode.computeQuaternion(camera.getViewMatrix());
            let endQuat = RenderViewMode.computeQuaternion(camera.getCustomViewMatrix(position, manipulatedCenter));

            cameraAnimator.setAnimationFrames([
                {
                    position: vec3.clone(camera.getCameraPosition()),
                    target: vec3.clone(camera.getCameraTarget()),
                    quat: startQuat
                },
                {
                    position: position,
                    target: manipulatedCenter,
                    quat: endQuat
                }
            ]);

            this.canvas.addAnimatedObject(this.getActiveView().getViewControls(), cameraAnimator);
        }
    }

    public setBottomView(aObject: IModel) {
        if (!this.sceneDocument) {
            return false;
        }

        let _sceneAPIBBox = this.sceneDocument.getSBoundingBox(); // use the scene bounding box by default

        let manipulatedCenter = _sceneAPIBBox.getCenter();
        // if the aObject is defined and has a method getBoundingBox, then we use the bounding box from it
        //@ts-ignore
        if (aObject && aObject !== undefined && aObject.getSBoundingBox !== undefined) {
            _sceneAPIBBox = aObject.getSBoundingBoxFromVisibleObjects();

            // get translation from manipulator transformation
            manipulatedCenter = this.sceneAPI.getActivityModule().transformByCurrentManipulatorTransformation(_sceneAPIBBox.getCenter());
        }

        // for stereo and vr mode set view to all mode's views, otherwise to active one only
        let views = this.getViewsToUpdateByViewMode();

        for (let i = 0; i !== views.length; i++) {
            let cameraAnimator = new RenderCameraAnimator(this.sceneAPI);
            cameraAnimator.setInterpolator(
                InterpolatorsFactory.create(
                    Quaternion.TypeName
                )
            );
            cameraAnimator.preventTexturesSwitching(true);

            let camera = views[i].getCamera();

            let position = vec3.clone(manipulatedCenter);
            position[1] = position[1] - _sceneAPIBBox.getDiameter();
            if (camera.getCameraPosition()[2] < position[2]) {
                position[2] = position[2] - 0.1; // prevent the 0 -1 0 view vector
            } else {
                position[2] = position[2] + 0.1; // prevent the 0 -1 0 view vector
            }

            let startQuat = RenderViewMode.computeQuaternion(camera.getViewMatrix());
            let endQuat = RenderViewMode.computeQuaternion(camera.getCustomViewMatrix(position, manipulatedCenter));

            cameraAnimator.setAnimationFrames([
                {
                    position: vec3.clone(camera.getCameraPosition()),
                    target: vec3.clone(camera.getCameraTarget()),
                    quat: startQuat
                },
                {
                    position: position,
                    target: manipulatedCenter,
                    quat: endQuat
                }
            ]);

            this.canvas.addAnimatedObject(this.getActiveView().getViewControls(), cameraAnimator);
        }
    }

    public setFrontView(aObject) {
        if (!this.sceneDocument) {
            return false;
        }

        let _sceneAPIBBox = this.sceneDocument.getSBoundingBox(); // use the scene bounding box by default

        let manipulatedCenter = _sceneAPIBBox.getCenter();
        // if the aObject is defined and has a method getBoundingBox, then we use the bounding box from it
        //@ts-ignore
        if (aObject && aObject !== undefined && aObject.getSBoundingBox !== undefined) {
            _sceneAPIBBox = aObject.getSBoundingBoxFromVisibleObjects();

            // get translation from manipulator transformation
            manipulatedCenter = this.sceneAPI.getActivityModule().transformByCurrentManipulatorTransformation(_sceneAPIBBox.getCenter());
        }

        let position = vec3.clone(manipulatedCenter);
        position[2] = position[2] + _sceneAPIBBox.getDiameter();

        // for stereo and vr mode set view to all mode's views, otherwise to active one only
        let views = this.getViewsToUpdateByViewMode();

        for (let i = 0; i !== views.length; i++) {
            let cameraAnimator = new RenderCameraAnimator(this.sceneAPI);
            cameraAnimator.setInterpolator(
                InterpolatorsFactory.create(
                    Quaternion.TypeName
                )
            );
            cameraAnimator.preventTexturesSwitching(true);

            let camera = views[i].getCamera();

            let startQuat = RenderViewMode.computeQuaternion(camera.getViewMatrix());
            let endQuat = RenderViewMode.computeQuaternion(camera.getCustomViewMatrix(position, manipulatedCenter));

            cameraAnimator.setAnimationFrames([
                {
                    position: vec3.clone(camera.getCameraPosition()),
                    target: vec3.clone(camera.getCameraTarget()),
                    quat: startQuat
                },
                {
                    position: position,
                    target: manipulatedCenter,
                    quat: endQuat
                }
            ]);

            this.canvas.addAnimatedObject(this.getActiveView().getViewControls(), cameraAnimator);
        }
    }

    public setBackView(aObject: IModel) {
        if (!this.sceneDocument) {
            return false;
        }

        let _sceneAPIBBox = this.sceneDocument.getSBoundingBox(); // use the scene bounding box by default

        let manipulatedCenter = _sceneAPIBBox.getCenter();
        // if the aObject is defined and has a method getBoundingBox, then we use the bounding box from it
        //@ts-ignore
        if (aObject && aObject !== undefined && aObject.getSBoundingBox !== undefined) {
            _sceneAPIBBox = aObject.getSBoundingBoxFromVisibleObjects();

            // get translation from manipulator transformation
            manipulatedCenter = this.sceneAPI.getActivityModule().transformByCurrentManipulatorTransformation(_sceneAPIBBox.getCenter());
        }

        let position = vec3.clone(manipulatedCenter);
        position[2] = position[2] - _sceneAPIBBox.getDiameter();

        // for stereo and vr mode set view to all mode's views, otherwise to active one only
        let views = this.getViewsToUpdateByViewMode();

        for (let i = 0; i !== views.length; i++) {
            let cameraAnimator = new RenderCameraAnimator(this.sceneAPI);
            cameraAnimator.setInterpolator(
                InterpolatorsFactory.create(
                    Quaternion.TypeName
                )
            );
            cameraAnimator.preventTexturesSwitching(true);

            let camera = views[i].getCamera();

            let startQuat = RenderViewMode.computeQuaternion(camera.getViewMatrix());
            let endQuat = RenderViewMode.computeQuaternion(camera.getCustomViewMatrix(position, manipulatedCenter));

            cameraAnimator.setAnimationFrames([
                {
                    position: vec3.clone(camera.getCameraPosition()),
                    target: vec3.clone(camera.getCameraTarget()),
                    quat: startQuat
                },
                {
                    position: position,
                    target: manipulatedCenter,
                    quat: endQuat
                }
            ]);

            this.canvas.addAnimatedObject(this.getActiveView().getViewControls(), cameraAnimator);
        }
    }

    public zoomToObject(aObject: IModel) {
        if (!aObject) {
            return false;
        }

        let _sceneAPIBBox = null;

        let manipulatedCenter = _sceneAPIBBox.getCenter();
        // if the aObject is defined and has a method getBoundingBox, then we use the bounding box from it
        //@ts-ignore
        if (aObject !== undefined && aObject.getSBoundingBox !== undefined) {
            _sceneAPIBBox = aObject.getSBoundingBoxFromVisibleObjects();

            // get translation from manipulator transformation
            manipulatedCenter = this.sceneAPI.getActivityModule().transformByCurrentManipulatorTransformation(_sceneAPIBBox.getCenter());
        }

        let position = vec3.clone(manipulatedCenter);
        vec3.scale(position, position, 2.0);

        // for stereo and vr mode set view to all mode's views, otherwise to active one only
        let views = this.getViewsToUpdateByViewMode();

        for (let i = 0; i !== views.length; i++) {
            let cameraAnimator = new RenderCameraAnimator(this.sceneAPI);
            cameraAnimator.setInterpolator(
                InterpolatorsFactory.create(
                    Quaternion.TypeName
                )
            );
            cameraAnimator.preventTexturesSwitching(true);

            let camera = views[i].getCamera();

            let startQuat = RenderViewMode.computeQuaternion(camera.getViewMatrix());
            let endQuat = RenderViewMode.computeQuaternion(camera.getCustomViewMatrix(position, manipulatedCenter));

            cameraAnimator.setAnimationFrames([
                {
                    position: vec3.clone(camera.getCameraPosition()),
                    target: vec3.clone(camera.getCameraTarget()),
                    quat: startQuat
                },
                {
                    position: position,
                    target: manipulatedCenter,
                    quat: endQuat
                }
            ]);

            this.canvas.addAnimatedObject(this.getActiveView().getViewControls(), cameraAnimator);
        }
    }

    public setViewMode(aViewModeType: ViewModeEnum) {

        this._viewModeType = aViewModeType;

        let oldMode = null;
        switch (aViewModeType) {
            case ViewModeEnum.SINGLE:
                oldMode = this._mode;
                if (oldMode) {
                    this.renderDocument.getRenderer().setViewFrustumCullingForTexturesSwitching(this.frustumCullingEnabled);
                }

                this._mode = new ViewModeSingle(this.renderDocument);

                if (oldMode) {
                    // restore controls from active view
                    let view = oldMode.getActiveView();
                    this._mode.setViewControls(view.getControls().getControlsType(), view.getControls());
                    this._mode.inheritObservers(oldMode);
                    oldMode.destroy();
                }

                break;

            case ViewModeEnum.STEREO:
                oldMode = this._mode;

                this._mode = new ViewModeStereo(this.renderDocument);
                this.frustumCullingEnabled = this.sceneAPI.renderSettingsModule.getViewFrustumCullingForTexturesSwitching();
                this.renderDocument.getRenderer().setViewFrustumCullingForTexturesSwitching(false);

                if (oldMode) {
                    // restore controls from active view
                    let view = oldMode.getActiveView();
                    this._mode.setViewControls(view.getControls().getControlsType(), view.getControls());
                    this._mode.inheritObservers(oldMode);
                    oldMode.destroy();
                }
                break;

            case ViewModeEnum.WEBVR:
                oldMode = this._mode;

                this._mode = new ViewModeWebVR(this.renderDocument);
                this.frustumCullingEnabled = this.sceneAPI.renderSettingsModule.getViewFrustumCullingForTexturesSwitching();
                this.renderDocument.getRenderer().setViewFrustumCullingForTexturesSwitching(false);

                if (oldMode) {
                    // restore controls from active view
                    let view = oldMode.getActiveView();
                    this._mode.setViewControls(view.getControls().getControlsType(), view.getControls());
                    this._mode.inheritObservers(oldMode);
                    oldMode.destroy();
                }
                break;

            case ViewModeEnum.QUAD:
                oldMode = this._mode;

                if (oldMode) {
                    this.renderDocument.getRenderer().setViewFrustumCullingForTexturesSwitching(this.frustumCullingEnabled);
                }

                this._mode = new ViewModeQuad(this.renderDocument);

                if (oldMode) {
                    // restore controls from active view
                    let view = oldMode.getActiveView();
                    this._mode.setViewControls(view.getControls().getControlsType(), view.getControls());
                    this._mode.inheritObservers(oldMode);
                    oldMode.destroy();
                }
                break;

            default :
                Debug.logError('Cannot set view mode: unknown type.');
                break;
        }
    }

    public resetSensor() {
        this._mode.resetSensor();
    }

    public setFollowHead(aFlag) {
        this._mode.setFollowHead(aFlag);
    }

    public getFollowHead(): boolean {
        return this._mode.getFollowHead();
    }

    public setStayOnGround(aFlag: boolean, aAltitude: number) {
        this._mode.setStayOnGround(aFlag, aAltitude);
    }

    public getStayOnGround(): boolean {
        return this._mode.getStayOnGround();
    }

    public initLogarithmicDepthBuffer() {
        return this._mode.initLogarithmicDepthBuffer();
    }

    public getViewModeType(): ViewModeEnum {
        return this._viewModeType;
    }

    public zoomViewAll() {
        let bbox = this.renderDocument.getBoundingBoxFromVisibleObjects();

        let activeView = this.getActiveView();
        if (!activeView) {
            return false;
        }

        let controls = activeView.getControls();
        if (!controls) {
            return false;
        }

        let camera = controls.getCamera();
        let ar = camera.getAspectRatio();
        let coef = (ar > 1 ? 1 : ar);
        let cameraDistance = ((bbox.getDiameter() / 4.0) / coef) / Math.tan(camera.getFieldOfView() / 2.0);

        let target = bbox.getCenter();
        let dir = camera.getCameraDirection();

        let eye = vec3.create();
        vec3.scaleAndAdd(eye, target, dir, -cameraDistance);

        camera.setCameraPositionAndTarget(eye, target);
    }

    /**
     *  Ask for pointer lock.
     */
    public setUsePointerLock(aViewControls: IViewControls) {
        /* jshint unused:false */
        this._mode.setUsePointerLock();
    }


    /***
     * Enable disable ortographic camera
     * @param aValue when true, use orthographic camera, otherwise use perspective
     * @returns {*|vec3}
     */
    public setUseOrthographicProjection(aValue: boolean): boolean {
        let activeView = this.getActiveView();
        if (!activeView) {
            return false;
        }

        let controls = activeView.getControls();
        if (!controls) {
            return false;
        }

        controls.getCamera().setUseOrthographicProjection(aValue);

        return true;
    }

    /* Get camera position vec3 */

    public getCameraPosition(): vec3 {
        return this._mode.getCameraPosition();
    }

    public getCameraTarget(): vec3 {
        return this._mode.getCameraTarget();
    }

    public getCameraFov(): number {
        return this._mode.getCameraFov();
    }

    public pickedSomething(): boolean {
        if (this.getActiveView() !== null) {
            let picker = new PrimitivePointPicker(this.renderDocument);
            return picker.pickSceneElement([]);
        }

        return false;
    }

    public isCameraAboveGeometry(): boolean {
        let activeView = this.getActiveView();
        if (!activeView) {
            return false;
        }

        let controls = activeView.getViewControls();
        if (!controls) {
            return false;
        }

        return controls.isCameraAboveGeometry();
    }

    public getViewWithIndex(aIndex: number): any {
        return this._mode.getViewWithIndex(aIndex);
    }

    public scheduleRedraw() {
        return this._mode.scheduleRedraw();
    }

    public scheduleResize(aContentSize: vec2) {
        return this._mode.scheduleResize(aContentSize);
    }

    public onClick(dragX: number, dragY: number, mouseButton: number, draggedDuringClick: boolean): void {
        this._mode.onClick(dragX, dragY, mouseButton, draggedDuringClick);
    }

    public onDoubleClick(dragX: number, dragY: number): void {
        this._mode.onDoubleClick(dragX, dragY);
    }

    public onDrag(dragX: number, dragY: number, delta: number, mouseButton: number): void {
        this._mode.onDrag(dragX, dragY, delta, mouseButton);
    }

    public onFocus(param): void {
        this._mode.onFocus(param);
    }

    public onKeyDown(key: string): void {
        this._mode.onKeyDown(key);
    }

    public onKeyPress(key: string, dragX: number, dragY: number): void {
        this._mode.onKeyPress(key, dragX, dragY);
    }

    public onKeyUp(key: string): void {
        this._mode.onKeyUp(key);
    }

    public onMouseIn(dragX: number, dragY: number, mouseButton: number): void {
        this._mode.onMouseIn(dragX, dragY, mouseButton);
    }

    public onMouseOut(dragX: number, dragY: number, mouseButton: number): void {
        this._mode.onMouseOut(dragX, dragY, mouseButton);
    }

    public onMousePress(dragX: number, dragY: number, mouseButton: number): void {
        this._mode.onMousePress(dragX, dragY, mouseButton);
    }

    public onMouseRelease(dragX: number, dragY: number, mouseButton: number, prevMouseButton: number): void {
        this._mode.onMouseRelease(dragX, dragY, mouseButton, prevMouseButton);
    }

    public onMove(dragX: number, dragY: number, mouseButton: number): void {
        this._mode.onMove(dragX, dragY, mouseButton);
    }

    public onSingleTap(downX: number | any, downY: number | any): void {
        this._mode.onSingleTap(downX, downY);
    }

    public onTouchEnd(middleElement: number, middleElement2: number, touch1: any): void {
        this._mode.onTouchEnd(middleElement, middleElement2, touch1);
    }

    public onTouchMove(po: number, po2: number, length: number, remaining: number): void {
        this._mode.onTouchMove(po, po2, length, remaining);
    }

    public onTouchStart(po: number, po2: number, length: number): void {
        this._mode.onTouchStart(po, po2, length);
    }

    public onWheel(dragX: number | any, dragY: number | any, delta: number, buttonState: number): void {
        this._mode.onWheel(dragX, dragY, delta, buttonState);
    }

    public render(): void {
        this._mode.render();
    }

    public tick(): void {
        this._mode.tick();
    }

    public viewChanged() {
    }

    public viewResized(aContentSize: vec2) {
    }
}

Scene.registerSceneAPIModule(ViewModeModule);
